// src/components/ImageUpload.tsx
import React, { ChangeEvent, useState, useEffect } from 'react';
import { secondaryButtonClass, dangerButtonClass } from '../constants';

interface ImageUploadProps {
    label: string;
    currentImage: string | null; // Base64 string or null
    onImageUpload: (base64Image: string | null) => void;
    recommendedSize?: string; // e.g., "600x800px"
    maxFileSizeMB?: number;
}

const ImageUpload: React.FC<ImageUploadProps> = ({
    label,
    currentImage,
    onImageUpload,
    recommendedSize,
    maxFileSizeMB = 2, // Default max size 2MB
}) => {
    const [preview, setPreview] = useState<string | null>(currentImage);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        setPreview(currentImage);
    }, [currentImage]);

    const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) {
            // onImageUpload(null); // Keep existing if no file selected or clear?
            // setPreview(null);
            return;
        }

        setError(null);

        // Validate file type
        if (!file.type.startsWith('image/')) {
            setError('Invalid file type. Please upload an image (PNG, JPG, GIF, WEBP).');
            event.target.value = ''; // Clear the input
            return;
        }

        // Validate file size
        if (file.size > maxFileSizeMB * 1024 * 1024) {
            setError(`File is too large. Maximum size is ${maxFileSizeMB}MB.`);
            event.target.value = ''; // Clear the input
            return;
        }

        const reader = new FileReader();
        reader.onloadend = () => {
            const base64String = reader.result as string;
            setPreview(base64String);
            onImageUpload(base64String);
        };
        reader.onerror = () => {
            setError('Failed to read file.');
            console.error('FileReader error');
        };
        reader.readAsDataURL(file);
        event.target.value = ''; // Clear the input so the same file can be re-selected
    };

    const handleClearImage = () => {
        setPreview(null);
        onImageUpload(null);
        setError(null);
    };

    const inputId = `image-upload-${label.replace(/\s+/g, '-').toLowerCase()}`;

    return (
        <div className="space-y-2">
            <label htmlFor={inputId} className="block text-sm font-medium text-gray-700">
                {label} {recommendedSize && <span className="text-xs text-gray-500"> (Recommended: {recommendedSize})</span>}
            </label>
            <div className="mt-1 flex items-center space-x-4">
                {preview ? (
                    <div className="shrink-0">
                        <img
                            src={preview}
                            alt="Current"
                            className="h-20 w-auto object-contain border border-gray-300 rounded-md"
                            onError={(e) => {
                                // Hide broken image; the outer conditional will keep showing this slot
                                (e.currentTarget as HTMLImageElement).style.display = 'none';
                                (e.currentTarget.nextElementSibling as HTMLElement | null)?.style.setProperty('display', 'flex');
                            }}
                        />
                        <div className="h-20 w-20 hidden items-center justify-center border-2 border-dashed border-gray-300 rounded-md text-gray-400 text-xs text-center p-1">
                            Load error
                        </div>
                    </div>
                ) : (
                    <div className="h-20 w-20 flex items-center justify-center border-2 border-dashed border-gray-300 rounded-md text-gray-400 text-xs">
                        No Image
                    </div>
                )}
                <div className="flex-grow">
                    <input
                        id={inputId}
                        type="file"
                        accept="image/png, image/jpeg, image/gif, image/webp"
                        onChange={handleFileChange}
                        className="block w-full text-sm text-gray-500
                                   file:mr-4 file:py-2 file:px-4
                                   file:rounded-md file:border-0
                                   file:text-sm file:font-semibold
                                   file:bg-blue-50 file:text-blue-700
                                   hover:file:bg-blue-100"
                        aria-describedby={error ? `${inputId}-error` : undefined}
                    />
                    {preview && (
                        <button
                            type="button"
                            onClick={handleClearImage}
                            className={`${dangerButtonClass} text-xs mt-2 px-3 py-1`}
                        >
                            Clear Image
                        </button>
                    )}
                </div>
            </div>
            {error && <p id={`${inputId}-error`} className="text-xs text-red-600 mt-1">{error}</p>}
             <p className="text-xs text-gray-500">Max file size: {maxFileSizeMB}MB. Allowed types: PNG, JPG, GIF, WEBP.</p>
        </div>
    );
};

export default ImageUpload;
